import { Box, Chip, Typography } from '@mui/material'
import React from 'react'
import { SaveStatus } from '../../types/save-status.types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

type DeleteStatusRowProps = {
  name: string
  status: SaveStatus
  errorMessage?: string
  disabled: boolean
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
}

export const DeleteStatusRow: React.FC<DeleteStatusRowProps> = ({ name, errorMessage, status, disabled, loadingLabel, completeLabel }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <div>
        <Typography>{name}</Typography>

        {errorMessage && (
          <Typography pl={2} color="error">
            {`${t('common.error')}: ${errorMessage}`}
          </Typography>
        )}
      </div>

      <DeleteStatusChip status={status} disabled={disabled} loadingLabel={loadingLabel} completeLabel={completeLabel} />
    </Box>
  )
}

type DeleteStatusChipProps = {
  status: SaveStatus
  disabled: boolean
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
}

const DeleteStatusChip: React.FC<DeleteStatusChipProps> = ({ status, disabled, loadingLabel, completeLabel }) => {
  const { t } = useCustomTranslation()

  switch (status) {
    case 'idle':
      return <Chip label={t('installation.delete.status.waiting')} color="info" disabled={disabled} />
    case 'error':
      return <Chip label={t('installation.delete.status.error')} color="error" disabled={disabled} />
    case 'loading':
      return <Chip label={loadingLabel ?? t('installation.delete.status.deleting')} color="secondary" disabled={disabled} />
    case 'complete':
      return <Chip label={completeLabel ?? t('installation.delete.status.deleted')} color="primary" disabled={disabled} />
  }
}
